import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useUser } from "@/utils/userContext/UserContext";

const AUTH_STORAGE_KEYS = ["harenaCowonder", "harenaCowonderAuth", "authToken"] as const;

function extractTokenFromStorage(): string | null {
  for (const key of AUTH_STORAGE_KEYS) {
    const raw = localStorage.getItem(key);
    if (!raw) continue;

    try {
      const parsed = JSON.parse(raw);
      if (parsed?.auth) return String(parsed.auth);
      if (parsed?.token) return String(parsed.token);
      if (parsed?.accessToken) return String(parsed.accessToken);
      if (typeof parsed === "string") return parsed;
    } catch {
      return raw;
    }
  }

  return null;
}

function isJwtExpired(token: string | null) {
  if (!token) return true;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return false;
    const payload = JSON.parse(window.atob(parts[1]));
    if (!payload?.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return false;
  }
}

export default function AuthExpiredListener() {
  const userContext = useUser();
  const alreadyHandlingRef = useRef(false);
  const CHECK_INTERVAL_MS = 15_000;

  useEffect(() => {
    const handler = (e: Event) => {
      if (alreadyHandlingRef.current) return;
      alreadyHandlingRef.current = true;

      const message =
        (e as CustomEvent)?.detail?.message ??
        "Sessão expirada. Faça login novamente.";

      try {
        userContext.logout();
      } catch (err) {
        console.error("Erro ao limpar contexto de usuário:", err);
        for (const key of AUTH_STORAGE_KEYS) {
          try {
            localStorage.removeItem(key);
          } catch {
            // best effort
          }
        }
      }

      try {
        toast.info(message, { position: "top-right", autoClose: 2500 });
      } catch {
        try {
          window.alert(message);
        } catch {
          // ignore
        }
      }

      window.setTimeout(() => {
        if (window.location.pathname !== "/login") {
          window.location.replace("/login");
        } else {
          alreadyHandlingRef.current = false;
        }
      }, 2500);
    };

    window.addEventListener("auth:expired", handler as EventListener);
    return () =>
      window.removeEventListener("auth:expired", handler as EventListener);
  }, [userContext]);

  useEffect(() => {
    const tick = () => {
      try {
        const token = extractTokenFromStorage();
        if (token && isJwtExpired(token)) {
          window.dispatchEvent(
            new CustomEvent("auth:expired", {
              detail: { message: "Sessão expirada (detecção pró-ativa)" },
            }),
          );
        }
      } catch (e) {
        console.error("Erro no proactive token check:", e);
      }
    };

    tick();
    const id = window.setInterval(tick, CHECK_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  return null;
}
